import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, message, Modal, Space,
  Typography
} from 'antd';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PackageGeneralInfo from './components/PackageGeneralInfo';
import PackageTierConfig from './components/PackageTierConfig';

import HeaderPage from 'common/components/HeaderPage';
import getDropdownTypeService from 'common/services/dropdownType';
import { createAddOnsPackageService, getAddOnPackageDetailService, putUpdateAddOnPackageService, } from 'common/services/packages';
import { IPackageAddOnRequest } from 'common/services/packages/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { addOnPackageDetailSchema } from 'common/utils/schemas';

export type FormTierValues = {
  name: string;
  general: { numberOfAgent: number; };
  knowledgeLimits: {
    numberOfFile: number;
    totalSizeLimit: number;
    numberOfWebsite: number;
  };
  endUserSides: {
    numberOfSession: number;
  },
  price: number;
};

export type FormValues = {
  name: string;
  duration: { value: number; label: string };
  isApplyPayg: boolean;
  tiers: FormTierValues;
  type: { value: number; label: string };
  descriptions: string;
};

const toCamelCase = (str: string) => str
  .toLowerCase()
  .split(' ')
  .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
  .join('');

const mapFormTierToPackageAddOnRequest = (
  data: FormValues,
): IPackageAddOnRequest => ({
  name: data.name || '',
  duration: data.duration?.value || 0,
  description: data.descriptions || '',
  addOnType: `${toCamelCase(data.type?.label)}` || '',
  numberOfAgent: data.tiers?.general?.numberOfAgent ?? 0,
  numberOfFile: data.tiers?.knowledgeLimits?.numberOfFile ?? 0,
  totalSizeLimit: data.tiers?.knowledgeLimits?.totalSizeLimit ?? 0,
  numberOfWebsite: data.tiers?.knowledgeLimits?.numberOfWebsite ?? 0,
  numberOfSession: data.tiers?.endUserSides?.numberOfSession ?? 0,
  price: data.tiers?.price ?? 0,
});

export const packageDurations = (t: TFunction) => [
  {
    label: t('package.aMonth'),
    value: 2,
  },
  {
    label: t('package.aYear'),
    value: 3,
  },

];

export const packageType = () => [
  {
    label: 'Agent',
    value: 1,
  },
  {
    label: 'Knowledge base',
    value: 2,
  },
  {
    label: 'Session',
    value: 3,
  },

];

const AddOnPackageDetail = () => {
  /* Hook */
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
    },
    resolver: yupResolver(addOnPackageDetailSchema),
  });

  const navigate = useNavigate();
  /* Search Params */
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));

  /* States */
  const [confirm, setConfirm] = useState<boolean>(false);

  /* React-query */
  const { isLoading: isPackageDetailLoading } = useQuery(
    ['getAddOnPackageDetail'],
    () => getAddOnPackageDetailService(idParams),
    {
      onSuccess: (data) => {
        method.reset({
          name: data.servicePackageAddOnData.name,
          duration: {
            label: packageDurations(t).find(
              (item) => item.value === data.servicePackageAddOnData.duration
            )?.label,
            value: data.servicePackageAddOnData.duration
          },
          descriptions: data.servicePackageAddOnData.description,
          type: {
            value: packageType().find(
              (item) => item.label.toLowerCase()
                === data.servicePackageAddOnData.addOnType.toLowerCase()
            )?.value || 0,
            label: packageType().find(
              (item) => item.label.toLowerCase().split(' ').join('')
                === data.servicePackageAddOnData.addOnType.toLowerCase()
            )?.label || '',
          },
          tiers: {
            general: {
              numberOfAgent: data.servicePackageAddOnData.numberOfAgent,
            },
            knowledgeLimits: {
              numberOfFile: data.servicePackageAddOnData.numberOfFile,
              totalSizeLimit: data.servicePackageAddOnData.totalSizeLimit,
              numberOfWebsite: data.servicePackageAddOnData.numberOfWebsite,
            },
            endUserSides: {
              numberOfSession: data.servicePackageAddOnData.numberOfSession,
            },
            price: data.servicePackageAddOnData.price,
          },
        });
      },
      enabled: !!searchParams.get('id'),
    }
  );

  const { mutate: updateAddOnPackageMutate } = useMutation(
    ['updatePackage', idParams],
    (params: IPackageAddOnRequest) => putUpdateAddOnPackageService(idParams, params),
    {
      onSuccess: () => {
        message.success(t('package.updateSuccess'));
        navigate(ROUTE_PATHS.ADD_ON_PACKAGE);
      },

      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError(error.field as keyof FormValues, { message: error.message || '' });
          });
          message.error(t('package.updateError'));
        } else {
          message.error(t('package.updateError'));
        }
      },
    }
  );

  const { data: customServices, isLoading: isCustomServiceLoading } = useQuery(
    ['getAllCustomServices'],
    () => getDropdownTypeService<any>('ai-assistant-service', {
      page: 1,
      limit: 200,
    }),
  );

  const { mutate: createMutateAddOn } = useMutation(
    ['createPackageAddOn'],
    async (data: IPackageAddOnRequest) => createAddOnsPackageService(data),
    {
      onSuccess: () => {
        message.success(t('add_on_package.success'));
        navigate(ROUTE_PATHS.ADD_ON_PACKAGE);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError(error.field as keyof FormValues, { message: error.message || '' });
          });
          message.error(t('add_on_package.error'));
        } else {
          message.error(t('add_on_package.error'));
        }
      }
    }
  );

  /* Functions */
  const handleSubmit = () => {
    // Formatted data to send to API
    const packageAddOnRequest:
      IPackageAddOnRequest = mapFormTierToPackageAddOnRequest(
        method.getValues() as FormValues,
      );
    if (idParams) {
      updateAddOnPackageMutate(packageAddOnRequest);
    } else {
      createMutateAddOn(packageAddOnRequest);
    }
  };

  /* Memos */
  const createPackageSteps = [
    {
      title: t('package.generalInfo'),
      content: <PackageGeneralInfo
        method={method}
        isLoading={isPackageDetailLoading || isCustomServiceLoading}
      />,
    },
    {
      title: t('package.tierConfig'),
      content: <PackageTierConfig
        method={method}
        customServicesData={customServices?.data}
        isLoading={isPackageDetailLoading || isCustomServiceLoading}
      />,
    },
  ];

  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.packageDetail')}
      />
      <div className="t-mainlayout_wrapper">
        <FormProvider {...method}>

          <div>
            {createPackageSteps.map((item) => (
              <div className="p-addOnPackageDetail_stepContainer" key={item.title}>
                <Typography.Text className="p-addOnPackageDetail_stepTitle">
                  {item.title}
                </Typography.Text>
                {item.content}
              </div>
            ))}
          </div>
          <Space direction="horizontal" align="end" className="u-mt-16 p-packageDetail_actions">
            <>
              <Button disabled={method.formState.errors && Object.keys(method.formState.errors).length > 0} onClick={() => { setConfirm(true); }}>{t('package.save')}</Button>
              <Modal
                title={t('package.saveChanges')}
                open={confirm}
                onOk={() => {
                  handleSubmit();
                  setConfirm(false);
                }}
                okText={t('package.save')}
                cancelText={t('system.cancel')}
                onCancel={() => { setConfirm(false); }}
              >
                {t('package.confirmSave')}
              </Modal>
            </>
          </Space>
        </FormProvider>
      </div>
    </>
  );
};

export default AddOnPackageDetail;
